import { ProductError, ProductErrorType } from '../../models';
import { LearnizMedia } from '../media';
import { MediaPlayerParams } from './media-player.interface';

export class TextBoxData {
  boxes: TextBox[];

  constructor(boxData?: TextBoxData) {
    this.boxes = boxData ? (boxData.boxes || []).map(b => new TextBox(b)) : [];
  }

  static validate(boxData: TextBoxData, payloadData: any): ProductError[] {
    if (!boxData) {
      return [{ type: ProductErrorType.MISSING, property: 'boxes', payload: { ...payloadData } }];
    }
    return boxData.boxes
      .map((box, boxIndex) =>
        TextBox.validate(box, {
          ...payloadData,
          boxIndex,
        }),
      )
      .reduce((flattenedArray: any[], subArray) => {
        flattenedArray.push(...subArray);
        return flattenedArray;
      }, []);
  }
}

export class TextBox {
  slug: string;
  boxDisplay?: BoxDisplay;
  boxDirection: BoxDirection;
  iconObject?: BoxIcon;
  contents: BoxContent[];
  title?: string;

  constructor(box?: TextBox) {
    this.slug = box ? box.slug : null;
    this.boxDisplay = box ? box.boxDisplay : null;
    this.boxDirection = box ? box.boxDirection : BoxDirection.REGULAR;
    this.iconObject = box ? box.iconObject : null;
    this.contents = box ? (box.contents || []).map(c => new BoxContent(c)) : [];
    this.title = box ? box.title : null;
  }

  static validate(box: TextBox, payloadData: any): ProductError[] {
    if (!box.contents || !box.contents.length) {
      return [{ type: ProductErrorType.MISSING, property: 'contents', payload: { ...payloadData } }];
    }
    return box.contents
      .map((content, contentIndex) =>
        BoxContent.validate(content, {
          ...payloadData,
          contentIndex,
        }),
      )
      .reduce((flattenedArray: any[], subArray) => {
        flattenedArray.push(...subArray);
        return flattenedArray;
      }, []);
  }
}

//can be string (~text or image src) or mediaplayer config or [a text AND an image source]
export class BoxContentTextImage {
  text?: string;
  imageSrc?: string;
  imageSize?: BoxImageSize;
  imageWidth?: string;
  verticalAlign?: ContentAlignment;
  horizontalAlign?: ContentAlignment;

  constructor(rawData?: BoxContentTextImage) {
    this.text = rawData?.text;
    this.imageSrc = rawData?.imageSrc;
    this.imageSize = rawData?.imageSize ?? BoxImageSize.SIZE_1_2; // is column size and not image size (i can't rename it due to conflict with external)
    this.imageWidth = rawData?.imageWidth ?? 'auto';
    this.verticalAlign = rawData?.verticalAlign ?? ContentAlignment.CENTER;
    this.horizontalAlign = rawData?.horizontalAlign ?? ContentAlignment.CENTER;
  }
}

export type BoxContentContent = string | LearnizMedia<MediaPlayerParams> | BoxContentTextImage;

export class BoxContent {
  subtitle?: string;
  content?: BoxContentContent;
  type: BoxContentType;

  constructor(content?: BoxContent) {
    if (content) {
      this.subtitle = content.subtitle;
      switch (content.type) {
        case BoxContentType.TEXT:
        case BoxContentType.IFRAME:
        case BoxContentType.IMAGE:
          this.content = content.content;
          break;
        case BoxContentType.MEDIA_PLAYER:
          this.content = new LearnizMedia<MediaPlayerParams>(<LearnizMedia<MediaPlayerParams>>content.content);
          break;
        case BoxContentType.IMAGE_TEXT:
        case BoxContentType.TEXT_IMAGE:
          this.content = new BoxContentTextImage(<BoxContentTextImage>content.content);
          break;
      }
      this.type = content.type;
    } else {
      this.subtitle = null;
      this.content = null;
      this.type = null;
    }
  }

  static validate(content: BoxContent, payloadData: any): ProductError[] {
    let primitivesError: ProductError[];
    switch (content.type) {
      case BoxContentType.TEXT:
      case BoxContentType.IFRAME:
      case BoxContentType.IMAGE:
        primitivesError = ProductError.validatePrimitiveTypes(content, ['content']) //
          .map(error => ({ ...error, payload: { ...payloadData } }));
        break;
      case BoxContentType.IMAGE_TEXT:
      case BoxContentType.TEXT_IMAGE:
        primitivesError = ProductError.validatePrimitiveTypes(content.content, ['text', 'imageSrc']) //
          .map(error => ({ ...error, payload: { ...payloadData } }));
        break;
    }
    return primitivesError;
  }
}

export class BoxIcon {
  icon: string;
  iconType: BoxIconType;

  constructor(boxIcon?: BoxIcon) {
    this.icon = boxIcon ? boxIcon.icon : null;
    this.iconType = boxIcon ? boxIcon.iconType : null;
  }
}

export enum BoxImageSize {
  /** image is 3/4 against 1/4 for the text */
  SIZE_3_1 = 'size-3-1',
  /** image is 2/3 against 1/3 for the text */
  SIZE_2_1 = 'size-2-1',
  /** image is 1/2 against 1/2 for the text */
  SIZE_1_1 = 'size-1-1',
  /** image is 1/3 against 2/3 for the text (default) */
  SIZE_1_2 = 'size-1-2',
  /** image is 1/4 against 3/4 for the text */
  SIZE_1_3 = 'size-1-3',
}

export enum BoxIconType {
  ICON = 'icon',
  IMAGE = 'image',
}

export enum BoxContentType {
  TEXT = 'text',
  IMAGE_TEXT = 'image-text',
  TEXT_IMAGE = 'text-image',
  IFRAME = 'iframe',
  MEDIA_PLAYER = 'media-player',
  IMAGE = 'image',
}

export enum BoxDirection {
  REGULAR = 'regular',
  REVERSE = 'reverse',
}

export enum BoxDisplay {
  CARD_ADVICE = 'card-advice',
}

export enum ContentAlignment {
  START = 'start',
  CENTER = 'center',
  END = 'end',
}

export function getBoxType(data: TextBoxData): BoxContentType {
  if (!data.boxes || data.boxes.length === 0) {
    throw new Error("Aucune box n'est disponible dans la donnée fournie.");
  }
  const firstBox = data.boxes[0];
  if (!firstBox.contents || firstBox.contents.length === 0) {
    throw new Error('La première box ne contient aucun contenu.');
  }
  return firstBox.contents[0].type;
}

export function convertBoxData(source: TextBoxData, target: TextBoxData): TextBoxData {
  const sourceType = getBoxType(source);
  const conversionTarget = getBoxType(target);

  switch (sourceType) {
    case BoxContentType.TEXT:
      if (conversionTarget === BoxContentType.TEXT_IMAGE || conversionTarget === BoxContentType.IMAGE_TEXT) {
        return convertTextToTextImage(source, target);
      }
      if (conversionTarget === BoxContentType.IMAGE) {
        return convertTextToImage(source, target);
      }
      if (conversionTarget === BoxContentType.TEXT) {
        return convertTextToText(source, target);
      }
      break;

    case BoxContentType.TEXT_IMAGE:
    case BoxContentType.IMAGE_TEXT:
      if (conversionTarget === BoxContentType.TEXT) {
        return convertTextImageToText(source, target);
      }
      if (conversionTarget === BoxContentType.IMAGE) {
        return convertTextImageToImage(source, target);
      }
      if (conversionTarget === BoxContentType.TEXT_IMAGE || conversionTarget === BoxContentType.IMAGE_TEXT) {
        return convertTextImageToTextImage(source, target);
      }
      break;

    case BoxContentType.IMAGE:
      if (conversionTarget === BoxContentType.TEXT) {
        return convertImageToText(source, target);
      }
      if (conversionTarget === BoxContentType.TEXT_IMAGE || conversionTarget === BoxContentType.IMAGE_TEXT) {
        return convertImageToTextImage(source, target);
      }
      if (conversionTarget === BoxContentType.IMAGE) {
        return source;
      }
      break;
  }

  throw new Error(`Conversion de ${sourceType} vers ${conversionTarget} non supportée.`);
}

export function convertTextImageToText(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: BoxContentType.TEXT });
      newContent.subtitle = srcContent.subtitle;
      newContent.content = (srcContent.content as BoxContentTextImage).text;
      return newContent;
    });
  });

  return target;
}

export function convertTextToTextImage(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    const boxType = currentBox.contents[0].type;

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: boxType });
      newContent.subtitle = srcContent.subtitle;
      newContent.content = new BoxContentTextImage({
        text: srcContent.content as string,
      });
      return newContent;
    });
  });

  return target;
}

export function convertTextImageToTextImage(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    const boxType = currentBox.contents[0].type;

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: boxType });
      newContent.subtitle = srcContent.subtitle;
      newContent.content = srcContent.content;
      return newContent;
    });
  });

  return target;
}

export function convertTextImageToImage(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: BoxContentType.IMAGE });
      newContent.subtitle = srcContent.subtitle;
      newContent.content = (srcContent.content as BoxContentTextImage).imageSrc;
      return newContent;
    });
  });

  return target;
}

export function convertImageToTextImage(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    const boxType = currentBox.contents[0].type;

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: boxType });
      newContent.subtitle = srcContent.subtitle;
      (newContent.content as BoxContentTextImage).imageSrc = srcContent.content as string;
      return newContent;
    });
  });

  return target;
}

export function convertTextToImage(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: BoxContentType.IMAGE });
      newContent.subtitle = srcContent.subtitle;
      return newContent;
    });
  });

  return target;
}

export function convertTextToText(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    currentBox.contents = srcBox.contents;
  });

  return target;
}

export function convertImageToText(source: TextBoxData, target: TextBoxData): TextBoxData {
  target.boxes = target.boxes || [];

  source.boxes.forEach((srcBox, index) => {
    const { box: currentBox, shouldPush } = getOrCreateTextBox(target, index, srcBox);
    if (shouldPush) {
      target.boxes.push(currentBox);
    }

    currentBox.contents = srcBox.contents.map(srcContent => {
      const newContent = new BoxContent({ type: BoxContentType.TEXT });
      newContent.subtitle = srcContent.subtitle;
      return newContent;
    });
  });

  return target;
}

function getOrCreateTextBox(
  target: TextBoxData,
  index: number,
  srcBox: TextBox,
): {
  box: TextBox;
  shouldPush: boolean;
} {
  let box: TextBox;
  let shouldPush = false;

  if (index < target.boxes.length) {
    box = target.boxes[index];
  } else {
    if (index > 0 && target.boxes[index - 1]) {
      box = Object.assign(new TextBox(), target.boxes[index - 1]);
    } else {
      box = new TextBox();
    }
    shouldPush = true;
  }

  box.slug = srcBox.slug;
  box.boxDirection = srcBox.boxDirection;
  box.title = srcBox.title;

  if (srcBox.boxDisplay === BoxDisplay.CARD_ADVICE && box.boxDisplay) {
    box.boxDisplay = BoxDisplay.CARD_ADVICE;
    if (srcBox?.iconObject?.icon) {
      box.iconObject = srcBox.iconObject;
    }
  }

  return { box, shouldPush };
}
